// File: src/pages/EventPreview.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import EventGallery from '../components/EventPreview/EventGallery';
import EventDetails from '../components/EventPreview/EventDetails';
import EventHeader from '../components/EventPreview/EventHeader';
import EventFooterAction from '../components/EventPreview/EventFooterActions';
import HostAccessBanner from '../components/EventPreview/HostAccessBanner';
import { getEventStartMs, getEventEndMs } from '../utils/eventTime';
import MembershipEventFooterAction from '../components/EventPreview/MembershipEventFooterAction';
import PostReservationSuccessSheet from '../components/EventPreview/PostReservationSuccessSheet';

import {
 FaMapMarkerAlt, FaChair,
     FaLock, FaWalking
  } from 'react-icons/fa';
  


import { AuthContext } from '../context/AuthContext';
import ConfirmRegistrationBottomSheet from '../components/ConfirmRegistrationBottomSheet';
import CancelRegistrationBottomSheet   from '../components/CancelRegistrationBottomSheet';
import EventTags from '../components/EventPreview/EventTags';
import EventDescription from '../components/EventPreview/EventDescription';
import NotInEventRadiusBottomSheet     from '../components/NotInEventRadiusBottomSheet';
import { EventAlreadyStartedBottomSheet } from '../components/NoRegistrationBottomSheet';
import mixerApi from '../utils/mixerApi'; // ⬅️ ADD THIS

import OrientationGateDeniedBottomSheet, { orientationForSheetFromClassic } from '../components/OrientationGateDeniedBottomSheet';
import AffinitySoftGateSheet from '../components/AffinitySoftGateSheet';
import AgeGateDeniedBottomSheet from '../components/AgeGateDeniedBottomSheet';

/* ────────────────────────────────────────────────
 *  Small geo helper – returns miles between 2 points
 * ──────────────────────────────────────────────── */
const haversineMi = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8; // Earth radius in miles
    const toRad = d => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  
const monthTxt = d => d.toLocaleString('en-US', { month: 'short' }).toUpperCase();


function userAgeFromLocal(currentUser) {
  const raw = currentUser?.birthdate || currentUser?.dateOfBirth || currentUser?.dob || localStorage.getItem('birthdate');
  if (!raw) return null;
  const d = new Date(raw); if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let age = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age--;
  return age;
}

function labelFromRange(min, max) {
  if (max == null) return `${min}+`;
  if (min === max) return `${min} only`;
  return `${min}–${max}`;
}

function shouldShowAffinitySoftGate(evt = {}) {
  const mode = String(evt?.mode || '').toLowerCase();

  // Affinity checkbox always triggers
  if (!!evt?.affinity) return true;

  if (mode === 'neutral') {
    const gg = (evt?.neutralGenderGate || '').toLowerCase();
    const o  = (evt?.neutralOrientation || '').toLowerCase();
    const mCap = Number(evt?.neutralMaleCap || 0);
    const fCap = Number(evt?.neutralFemaleCap || 0);

    // Only treat as restricted if explicitly set
    const genderRestricted = gg && gg !== 'all';
    const orientationRestricted = o && o !== 'open';
    const hasCaps = mCap > 0 || fCap > 0;

    return genderRestricted || orientationRestricted || hasCaps;
  }

  if (mode === 'matchmaking') {
    const g = (evt?.classicGenderGate || '').toLowerCase();
    const o = (evt?.classicOrientation || '').toLowerCase();
    const m = Number(evt?.maleCap || 0);
    const f = Number(evt?.femaleCap || 0);

    const genderRestricted = g && g !== 'all';
    const orientationRestricted = o && o !== 'open';
    const hasCaps = m > 0 || f > 0;

    return genderRestricted || orientationRestricted || hasCaps;
  }

  if (mode === 'mixer' || evt?.type === 'mixer') {
    const allowed = Array.isArray(evt?.allowedGenders) ? evt.allowedGenders.filter(Boolean) : [];
    const o = (evt?.orientationType || '').toLowerCase();
    const g = (evt?.genderGate || '').toLowerCase();
    const m = Number(evt?.maleCap || 0);
    const f = Number(evt?.femaleCap || 0);

    const genderRestricted = allowed.length > 0 || (g && g !== 'all');
    const orientationRestricted = o && o !== 'open';
    const hasCaps = m > 0 || f > 0;

    return genderRestricted || orientationRestricted || hasCaps;
  }

  if (mode === 'reveal') return true;

  return false;
}

/* ────────────────────────────────────────────────
 *  Main component
 * ──────────────────────────────────────────────── */
export default function EventCardPage() {
  /* ───────── ROUTER / CONTEXT ───────────────── */
  const { eventId }   = useParams();
  const { state }     = useLocation();
  const navigate      = useNavigate();
  const { currentUser } = useContext(AuthContext);
   const [error, setError] = useState('');
  const [registerBusy, setRegisterBusy] = useState(false);  // 👈 NEW
  const [ticketRefreshKey, setTicketRefreshKey] = useState(0);
  const [hasTicket, setHasTicket] = useState(false);
const [membership, setMembership] = useState(null);


const [
  showReservationSuccess,
  setShowReservationSuccess,
] = useState(false);

const [
  reservationSuccessGathering,
  setReservationSuccessGathering,
] = useState(null);


  /* ───────── LOCAL STATE ─────────────────────── */
  const [event,            setEvent]            = useState(state?.event || null);
  const [eventStarted, setEventStarted] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const [isRegistered,     setRegistered]       = useState(false);
  const [isSaved,          setSaved]            = useState(false);
  const [hostAccess, setHostAccess] = useState(false);

  const [occCount,         setOcc]              = useState(0);
  const [showConfirmSheet, setShowConfirmSheet] = useState(false);
  const [showCancelSheet,  setShowCancelSheet]  = useState(false);
  const [showQuiz,         setShowQuiz]         = useState(false);
  const [showGeoSheet,     setShowGeoSheet]     = useState(false);
  const [showRegFailSheet, setShowRegFailSheet] = useState(false);
const [showOrientationDenied, setShowOrientationDenied] = useState(false);
const [deniedOrientation, setDeniedOrientation] = useState('open'); // ✅ add back (used by deny sheet)

const [showAffinitySoftGate, setShowAffinitySoftGate] = useState(false);
const [affinitySoftGateCtx, setAffinitySoftGateCtx] = useState(null); // { affinity, restricted }

const [showAgeDenied, setShowAgeDenied] = useState(false);
const [ageGateLabel, setAgeGateLabel] = useState('18+');

  const [galIdx,           setGalIdx]           = useState(0);
  const galRef = useRef(null);
  const topRef = useRef(null);
  /* ───────── DEBUG ───────────────────────────── */
  useEffect(() => {
    console.log('[EventCardPage] eventId =', eventId);
  }, [eventId]);


    /* ───────── FORCE SCROLL TO TOP ON MOUNT/NAV ───────── */
    useEffect(() => {
      // prevent browser from restoring old scroll position
      const prev = window.history.scrollRestoration;
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      // jump to top immediately, then again next frame (covers late layout)
      window.scrollTo(0, 0);
      requestAnimationFrame(() => window.scrollTo(0, 0));
      // also scroll the root container if the app wraps content
      document.getElementById('root')?.scrollTo?.(0, 0);
      return () => {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = prev || 'auto';
        }
      };
    }, [eventId]);

/* ───────── EVENT / PORTAL TIMING WATCHER ───────── */
useEffect(() => {
  if (!event) return;

  const updateTiming = () => {
    const startMs = getEventStartMs(event);

    if (!startMs) {
      setPortalOpen(false);
      setEventStarted(false);
      return;
    }

    const now = Date.now();

    // Portal opens exactly 1 hour before gathering start.
    const portalOpenMs = startMs - 60 * 60 * 1000;

    setPortalOpen(now >= portalOpenMs);

    // Registration closes only at the ACTUAL gathering start time.
    setEventStarted(now >= startMs);
  };

  updateTiming();

  const id = setInterval(updateTiming, 15_000);

  return () => clearInterval(id);
}, [event]);

  async function fetchMyTicket() {
    const token = localStorage.getItem('accessToken');
    if (!token) { setHasTicket(false); return; }
  
    try {
      const isMixer = String(eventId).startsWith('mixer-');
      const cleanId = isMixer ? String(eventId).replace('mixer-', '') : String(eventId);
      const url = `${process.env.REACT_APP_API_URL}/tickets/${cleanId}/mine?ts=${Date.now()}`;
  
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      const t = res.data?.userTicket || (res.data?.ok ? null : res.data) || null;
      setHasTicket(!!t);
    } catch {
      setHasTicket(false);
    }
  }

  useEffect(() => {
    if (!eventId) return;
    fetchMyTicket();
  }, [eventId, event?._id]);
  
  // listen for ticket invalidation or refresh (e.g., purchase / cancel)
useEffect(() => {
  const cur = String(event?._id || String(eventId).replace(/^mixer-/, ''));
  const onInvalidate = (e) => {
    const id = String(e.detail?.eventId || '');
    if (id && id === cur) fetchMyTicket();
  };
  const onRefresh = (e) => {
    const id = String(e.detail?.eventId || '');
    if (id && id === cur) fetchMyTicket();
  };

  window.addEventListener('ticket:invalidate', onInvalidate);
  window.addEventListener('ticket:refresh', onRefresh);
  return () => {
    window.removeEventListener('ticket:invalidate', onInvalidate);
    window.removeEventListener('ticket:refresh', onRefresh);
  };
}, [event?._id, eventId]);
  /* ───────── AUTH HELPER ─────────────────────── */
  const getAuth = () => {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}');
    return {
      userId: currentUser?._id
        || auth.user?._id
        || localStorage.getItem('userId')
        || '',
      token : auth.accessToken
        || localStorage.getItem('accessToken')
        || '',
    };
  };

  // helper near top
const isMixerEvent = (evt, id) =>
  evt?.type === 'mixer' || evt?.mode === 'mixer' || String(id).startsWith('mixer-');


  const { userId } = getAuth();

useEffect(() => {
  async function fetchMembership() {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/subscriptions/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMembership(data || null);
    } catch (err) {
      console.warn('[membership fetch]', err?.response?.data || err.message);
      setMembership(null);
    }
  }

  fetchMembership();
}, [currentUser?._id]);

  /* fetch & sockets */
   /* ──────────────────────────────────────────────────────────────
   Fetch the event once, then keep it in-sync in real-time
   ────────────────────────────────────────────────────────────── */
   useEffect(() => {
    const { userId: uid, token } = getAuth();
    let isAlive = true;
    let socket;
  
    const loadEvent = async () => {
      try {
        const isMixerEvent = eventId.startsWith('mixer-');
  
        const { data } = await axios.get(
          isMixerEvent
            ? `${process.env.REACT_APP_API_URL}/mixer/events/${eventId.replace('mixer-', '')}`
            : `${process.env.REACT_APP_API_URL}/events/${eventId}`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
        );
  
        if (!isAlive) return;
  
        setEvent(prev => {
          if (!prev) return { ...data, type: isMixerEvent ? 'mixer' : 'standard' };
          return {
            ...data,
            type: isMixerEvent ? 'mixer' : 'standard',
            promoterId: prev.promoterId && typeof data.promoterId === 'string'
              ? prev.promoterId
              : data.promoterId,
          };
        });
  
        setOcc(data.occupancyCount ?? 0);

        const attendeeIds = [
          ...(data.attendeeIds || []),
          ...(Array.isArray(data.attendees) ? data.attendees.map(a => a._id || a) : []),
        ].map(String);
        
        const savers = (data.savedUserIds || []).map(String);
        const uid = String(currentUser?._id || localStorage.getItem('userId') || '');
        
        setRegistered(attendeeIds.includes(uid));
        setSaved(savers.includes(uid));        
      } catch (err) {
        console.error('[event-fetch]', err);
      }
    };
  
    loadEvent();

  }, [eventId, currentUser?._id]);
  

  const fetchEvent = async () => {
    const isMixerEvent = eventId.startsWith('mixer-');
    const cleanEventId = isMixerEvent ? eventId.replace('mixer-', '') : eventId;
    const url = isMixerEvent
      ? `${process.env.REACT_APP_API_URL}/mixer/events/${cleanEventId}`
      : `${process.env.REACT_APP_API_URL}/events/${cleanEventId}`;
  
    try {
      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
  
      setEvent(prev => ({
        ...data,
        type: isMixerEvent ? 'mixer' : 'standard',
        promoterId:
          prev?.promoterId && typeof data.promoterId === 'string'
            ? prev.promoterId
            : data.promoterId,
      }));
  
      setOcc(data.occupancyCount ?? 0);
  
      const attendeeIds =
        (data.attendeeIds ?? data.rsvpUserIds) ??
        (Array.isArray(data.attendees) ? data.attendees.map(a => a._id || a) : []);
  
      const savers = (data.savedUserIds ?? []).map(String);
      const { userId: uid } = getAuth();
  
      setRegistered(attendeeIds.map(String).includes(String(uid)));
      setSaved(savers.includes(String(uid)));
    } catch (err) {
      console.error('[event-fetch]', err);
    }
  };
  
  

  // 📍 put this just below the fetchEvent helper (or anywhere after the hooks)
useEffect(() => {
  if (!event) return;

  const { userId: me } = getAuth();

  const normalizeEmail = (s) => String(s || '').trim().toLowerCase();

  const submittedByMatch =
    !!event?.submittedBy && String(event.submittedBy) === String(me);

  const promoterEmail =
    normalizeEmail(
      event?.promoterId?.email ||
      ''
    );

  const currentUserEmail =
    normalizeEmail(
      currentUser?.email ||
      ''
    );

const assignedPromoterMatch =
  !!promoterEmail &&
  !!currentUserEmail &&
  promoterEmail === currentUserEmail;
  
  const isHost = submittedByMatch || assignedPromoterMatch;
  setHostAccess(isHost);

  const ids = Array.isArray(event.attendees)
    ? event.attendees.map(a => String(a?._id ?? a))
    : Array.isArray(event.attendeeIds)
    ? event.attendeeIds.map(String)
    : [];

  const savedIds = (event.savedUserIds ?? []).map(String);

  // host should not be treated as registered unless actually in attendees
  setRegistered(ids.includes(String(me)));
  setSaved(savedIds.includes(String(me)));
  setOcc(typeof event.occupancyCount === 'number' ? event.occupancyCount : ids.length);
}, [event, currentUser]);
  
const location = useLocation();

useEffect(() => {
  if (location.state?.justRegistered) {
    const bookedGatheringId = String(
      event?._id || eventId
    ).replace(/^mixer-/, "");

    fetchEvent().then(() => {
      setRegistered(true);

      openReservationSuccess(
        bookedGatheringId,
        location.state?.event || event,
        location.state?.isFirstReservation ===
          true
      );
    });

    navigate(
      location.pathname,
      {
        replace: true,
        state: {},
      }
    );
  }
}, [
  location.state?.justRegistered,
  location.state?.event,
  location.pathname,
  event?._id,
  eventId,
  navigate,
]);


  if (!event)
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;

  /* destructure */
  const {
    name,
    description,
    address,
    latitude,
    longitude,
    maxGroupSize = 0,
    eventDate,
    startTime12,
    endTime12,
    galleryImages = [],
    promoterId = {},
    gameMode = '',
    icebreakers = [],
    attendees      = [],            // ← mirrors EventCard.jsx
    attendeeIds    = [],            // (optional – if your API returns it)
    attendeeCount,
    purposeTag = '',
  } = event;

// Hetero Mixer flag: Mixer + Dating + orientationType=heterosexual
const isHeteroMixer =
  (event?.type === 'mixer' || event?.mode === 'mixer' || String(eventId).startsWith('mixer-')) &&
  String(event?.purposeTag || '') === 'Dating' &&
  String(event?.orientationType || '').toLowerCase() === 'heterosexual';


/* ---------- safe promoter helper ---------- */
const promoter = (event.promoterId && typeof event.promoterId === 'object')
  ? event.promoterId      // populated object from API
  : {};                   // no promoter → empty object

const hostImg = promoter.imageUrl
  ? (promoter.imageUrl.startsWith('http')
        ? promoter.imageUrl
        : `${process.env.REACT_APP_API_URL}${promoter.imageUrl}`)
  : 'https://i.pravatar.cc/40?u=host';

  
// Hide any legacy mileage data supplied by cached events or route state.
const displayEvent = {
  ...event,
  distanceMiles: null,
  distanceKm: null,
  distanceMeters: null,
};

  const d = eventDate ? new Date(eventDate) : null;
  const badgeMonth = d ? monthTxt(d) : '';
  const badgeDay   = d ? d.getDate() : '';


 /* ───── helper numbers used in the header pill ───── */
 const cap    = typeof event.capacity     === 'number' ? event.capacity     : 0;
const maxGrp = typeof event.maxGroupSize === 'number' ? event.maxGroupSize : 0;
const capacity = cap || maxGrp || 0;          // 0 ⇢ unlimited

const attendeeTotal = event.attendeeCount
       ?? (Array.isArray(event.attendees)   ? event.attendees.length
       :  Array.isArray(event.attendeeIds) ? event.attendeeIds.length : 0);
  
   const remaining  = capacity > 0 ? Math.max(capacity - attendeeTotal, 0) : 0;
   const isFull     = capacity > 0 && remaining === 0;
   
// how many people have actually registered
const headCount = attendeeTotal;

/* naïve “live” check */

/* ---- Tag strip data ---- */
const requiresReg = true; // always require registration for entry
const isUpcoming  = !eventStarted;   // change if you have a dedicated flag

// -----------------------------------------------
// Registration, purpose, venue, and event-status tags
// -----------------------------------------------

const tagChips = [
  // registration   (always show)
  {
    label : requiresReg ? 'Registration Required' : 'No Registration Needed',
    icon  : requiresReg ? FaLock : FaWalking,
  },

  // event purpose  (if any)
  ...(purposeTag ? [{ label: purposeTag, icon: null }] : []),

  // hosted-at tag
  ...(event.hostedAt
      ? [{ label: event.hostedAt, icon: FaChair }]
      : []),

  // live / upcoming flag
  ...(eventStarted
      ? [{ label: 'Live',     icon: null }]
      : isUpcoming
      ? [{ label: 'Upcoming', icon: null }]
      : []),
];

  /* actions */
  /* ───────── actions (save / register) ───────────────────────── */

/** Guard: kicks unauthenticated users to /login */
const requireAuth = () => {
    const { userId: uid, token } = getAuth()
    if (!uid || !token) {
      navigate('/login', { state: { redirectAfter: window.location.pathname } });
      return false;
    }
    return { uid, token };
  };
  
  /* SAVE / UNSAVE (optimistic) */
  /* SAVE / UNSAVE (optimistic) */
async function handleSave(e) {
  e.stopPropagation();
  const auth = requireAuth();
  if (!auth) return;

  if (registerBusy) return;       // guard against double-clicks
  setRegisterBusy(true);          // 🔒 start
  try {
    const next = !isSaved;
    setSaved(next);               // optimistic

    const isMixerEvent = event.type === 'mixer' || String(eventId).startsWith('mixer-');
    const cleanEventId = isMixerEvent ? eventId.replace('mixer-', '') : eventId;

    const saveUrl = `${process.env.REACT_APP_API_URL}/${
      isMixerEvent ? 'mixer/events' : 'events'
    }/${cleanEventId}/${next ? 'save' : 'unsave'}`;

    await axios.post(
      saveUrl,
      { userId: auth.uid },
      { headers: { Authorization: `Bearer ${auth.token}` } },
    );

    // pull fresh (server is source of truth)
    await fetchEvent();
  } catch (err) {
    console.error('[save]', err);
    setSaved(prev => !prev); // rollback on error
  } finally {
    setRegisterBusy(false);  // 🔓 ALWAYS clear
  }
}

// Map Mixer audience settings → sheet label (‘gay_men’ | ‘gay_women’ | ‘straight’ | ‘open’)
function orientationForSheetFromMixer(evt = {}) {
  const p = String(evt.purposeTag || '').toLowerCase();
  if (p === 'dating') {
    const o = String(evt.orientationType || '').toLowerCase();
    if (o === 'gay_men')       return 'gay_men';
    if (o === 'gay_women')     return 'gay_women';
    if (o === 'heterosexual')  return 'straight';
    return 'open';
  }
  if (p === 'friendship' || p === 'networking') {
    const g = String(evt.genderGate || 'all').toLowerCase();
    if (g === 'men_only')      return 'gay_men';
    if (g === 'women_only')    return 'gay_women';
    return 'open';
  }
  return 'open';
}

function deriveGenderGate(evt = {}) {
  // Prefer explicit event.genderGate if present
  const g = String(evt.genderGate || '').toLowerCase();
  if (g === 'men_only' || g === 'menonly') return 'men_only';
  if (g === 'women_only' || g === 'womenonly') return 'women_only';

  // Fallback: infer from allowedGenders (if provided for mixers)
  const allowed = Array.isArray(evt.allowedGenders) ? evt.allowedGenders.map(s => String(s).toLowerCase()) : [];
  const hasM = allowed.includes('male');
  const hasF = allowed.includes('female');
  if (hasM && !hasF) return 'men_only';
  if (!hasM && hasF) return 'women_only';

  return 'all';
}

// Map NEUTRAL audience settings -> sheet key ('gay_men' | 'gay_women' | 'straight' | 'open')
function orientationForSheetFromNeutral(evt = {}) {
  const gg = String(evt?.neutralGenderGate || 'all').toLowerCase();
  if (gg === 'men_only') return 'gay_men';     // sheet displays "men"
  if (gg === 'women_only') return 'gay_women'; // sheet displays "women"

  const o = String(evt?.neutralOrientation || 'open').toLowerCase();
  if (o === 'gay_men') return 'gay_men';
  if (o === 'gay_women') return 'gay_women';
  if (o === 'straight') return 'straight';
  return 'open';
}

  /* ───────── REVEAL HELPERS ───────── */
function isRevealEvent(evt) {
  return String(evt?.mode || '').toLowerCase() === 'reveal';
}
function normalizeGender(val) {
  const s = String(val || '').trim().toLowerCase();
  if (!s) return '';
  const compact = s.replace(/[\s_-]/g, '');

  // Men (includes trans men)
  if (
    s === 'male' || s === 'man' || s === 'm' ||
    s === 'cis male' || s === 'cis man' ||
    compact === 'transman' || s === 'trans man' ||
    compact === 'ftm' || compact === 'f2m' ||
    compact.includes('femaletomale') || compact.includes('transmasc')
  ) return 'male';

  // Women (includes trans women)
  if (
    s === 'female' || s === 'woman' || s === 'f' ||
    s === 'cis female' || s === 'cis woman' ||
    compact === 'transwoman' || s === 'trans woman' ||
    compact === 'mtf' || compact === 'm2f' ||
    compact.includes('maletofemale') || compact.includes('transfem') || compact.includes('transfeminine')
  ) return 'female';

  // Nonbinary buckets
  if (
    s === 'nonbinary' || s === 'non-binary' || s === 'nb' || s === 'enby' ||
    s === 'genderqueer' || s === 'gender fluid' || s === 'genderfluid' ||
    s === 'agender'
  ) return 'nonbinary';

  return s;
}

/**
 * Enforce revealMode.orientation hard gate:
 * - gay_men    -> only 'male'
 * - gay_women  -> only 'female'
 * - straight   -> allow 'male' or 'female' (block others)
 * - bisexual/open -> allow anyone
 */
function passesRevealGenderGate(evt, userGender) {
  const normOrientation = (() => {
    const s = String(evt?.revealMode?.orientation || '').trim().toLowerCase();
    const flat = s.replace(/[^a-z]/g, '');
    if (flat === 'gaymen' || flat === 'menlovingmen' || flat === 'mlm') return 'gay_men';
    if (flat === 'gaywomen' || flat === 'lesbian' || flat === 'wlw' || flat === 'womenlovingwomen') return 'gay_women';
    if (flat === 'straight' || flat === 'hetero' || flat === 'heterosexual' || flat === 'menandwomen') return 'straight';
    if (flat === 'bisexual' || flat === 'bi') return 'bisexual';
    if (flat === 'open' || flat === 'any' || flat === 'everyone' || flat === 'all') return 'open';
    return flat || '';
  })();

  const g = normalizeGender(userGender);

  if (!normOrientation) return true; // no config -> don't block
  switch (normOrientation) {
    case 'gay_men':   return g === 'male';
    case 'gay_women': return g === 'female';
    case 'straight':  return g === 'male' || g === 'female';
    case 'bisexual':
    case 'open':
    default:          return true;
  }
}

/** Nice message for the block */
function revealGateMessage(evt) {
  const o = String(evt?.revealMode?.orientation || '').toLowerCase();
  if (o === 'gay_men')   return 'This Reveal event is for women-free gay men only.';
  if (o === 'gay_women') return 'This Reveal event is for men-free gay women only.';
  if (o === 'straight')  return 'This Reveal event is for men and women only.';
  return 'You do not meet the entry criteria for this Reveal event.';
}
function orientationKeyForEvent(evt) {
  return `reveal:softGateAck:${String(evt?._id || eventId)}`;
}

const hasActiveMembership =
  membership?.membershipActive === true &&
  membership?.canReserve !== false;

function wasFirstReservationBeforeBooking() {
  if (!membership) {
    return false;
  }

  return Boolean(
    membership?.hasUpcomingReservation !== true &&
      membership?.hasEverAttendedGathering !== true &&
      !currentUser?.firstReservationAt &&
      Number(
        membership?.membershipReservationsUsed || 0
      ) === 0
  );
}

function openReservationSuccess(
  gatheringId,
  gatheringData = event,
  isFirstReservation = false
) {
  const normalizedGatheringId = String(
    gatheringId || gatheringData?._id || ""
  ).trim();

  if (!normalizedGatheringId) {
    return;
  }

  // Every successful reservation still tells the rest of the app
  // to refresh its ticket and membership state.
  window.dispatchEvent(
    new CustomEvent(
      "tribe:reservation-success",
      {
        detail: {
          gatheringId: normalizedGatheringId,
          gathering:
            gatheringData || event || null,
          isFirstReservation:
            isFirstReservation === true,
        },
      }
    )
  );

  // Only the separate Home first-booking success is first-time-only.
  if (isFirstReservation === true) {
    localStorage.setItem(
      "tribePendingReservationSuccess",
      normalizedGatheringId
    );
  } else {
    localStorage.removeItem(
      "tribePendingReservationSuccess"
    );
  }

  // The normal "You reserved your spot" confirmation plays after
  // every successful reservation.
  setReservationSuccessGathering(
    gatheringData || event
  );

  setShowReservationSuccess(true);
}
async function reserveWithMembership() {
  const auth = requireAuth();
  if (!auth) return;

  try {
    setRegisterBusy(true);

    const firstReservationFallback =
      wasFirstReservationBeforeBooking();

    const cleanEventId =
      event?.type === "mixer" ||
      String(eventId).startsWith("mixer-")
        ? String(eventId).replace(
            "mixer-",
            ""
          )
        : String(event?._id || eventId);

    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/tickets/${cleanEventId}`,
      {
        amount: 0,
        method: "membership",
      },
      {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      }
    );

    if (!data?.ok) {
      alert(
        data?.message ||
          "Could not reserve your spot."
      );
      return;
    }

    openReservationSuccess(
      cleanEventId,
      data?.event
        ? {
            ...event,
            ...data.event,
          }
        : event,
      typeof data?.isFirstReservation ===
        "boolean"
        ? data.isFirstReservation
        : firstReservationFallback
    );

    setHasTicket(true);
    setRegistered(true);

    /*
      Update the local membership count so another
      reservation during this session is not treated
      as the user's first active reservation.
    */
    setMembership(
      (previousMembership) => {
        if (!previousMembership) {
          return previousMembership;
        }

        const limit = Math.max(
          1,
          Number(
            previousMembership
              .membershipReservationLimit ||
              3
          )
        );

        const currentUsed = Math.max(
          0,
          Number(
            previousMembership
              .membershipReservationsUsed ||
              0
          )
        );

        const nextUsed = currentUsed + 1;

        return {
          ...previousMembership,
          membershipReservationsUsed:
            nextUsed,
          membershipReservationsRemaining:
            Math.max(
              0,
              limit - nextUsed
            ),
        };
      }
    );

    window.dispatchEvent(
      new CustomEvent("ticket:refresh", {
        detail: {
          eventId: String(cleanEventId),
        },
      })
    );

    await fetchEvent();
  } catch (err) {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Could not reserve your spot.";

    alert(message);
  } finally {
    setRegisterBusy(false);
  }
}

async function runReservationPreflight(e) {
  e?.stopPropagation?.();

  const auth = requireAuth();
  if (!auth) return false;

  if (registerBusy) return false;

  setRegisterBusy(true);
  const finish = () => setRegisterBusy(false);

  if (hostAccess) {
    finish();
    return false;
  }

  if (event?.ageGateOn) {
    const ua = userAgeFromLocal(currentUser);
    const min = Number(event?.ageMin ?? 18);
    const max =
      event?.ageMax == null
        ? null
        : Number(event?.ageMax);

    const fail =
      ua == null ||
      ua < min ||
      (max != null && ua > max);

    if (fail) {
      setAgeGateLabel(labelFromRange(min, max));
      setShowAgeDenied(true);
      finish();
      return false;
    }
  }

  const isNeutral =
    String(event?.mode || '').toLowerCase() === 'neutral';

  if (isNeutral) {
    const userG = normalizeGender(
      currentUser?.gender ||
        localStorage.getItem('gender') ||
        ''
    );

    const gg = String(
      event?.neutralGenderGate || 'all'
    ).toLowerCase();

    if (
      (gg === 'men_only' && userG !== 'male') ||
      (gg === 'women_only' && userG !== 'female')
    ) {
      setDeniedOrientation(
        orientationForSheetFromNeutral(event)
      );
      setShowOrientationDenied(true);
      finish();
      return false;
    }
  }

  const isClassic =
    String(event?.mode || '').toLowerCase() ===
    'matchmaking';

  if (isClassic) {
    const userG = normalizeGender(
      currentUser?.gender ||
        localStorage.getItem('gender') ||
        ''
    );

    const allowed = (() => {
      const purpose = String(
        event?.purposeTag || ''
      ).toLowerCase();

      if (purpose === 'dating') {
        const orientation = String(
          event?.classicOrientation || 'open'
        ).toLowerCase();

        if (orientation === 'gay_men') {
          return ['male'];
        }

        if (orientation === 'gay_women') {
          return ['female'];
        }

        if (
          orientation === 'straight' ||
          orientation === 'heterosexual'
        ) {
          return ['male', 'female'];
        }

        return ['male', 'female', 'nonbinary'];
      }

      if (
        purpose === 'friendship' ||
        purpose === 'networking'
      ) {
        const gate = String(
          event?.classicGenderGate || 'all'
        ).toLowerCase();

        if (gate === 'men_only') {
          return ['male'];
        }

        if (gate === 'women_only') {
          return ['female'];
        }
      }

      return ['male', 'female', 'nonbinary'];
    })();

    if (!allowed.includes(userG)) {
      setDeniedOrientation(
        orientationForSheetFromClassic(event)
      );
      setShowOrientationDenied(true);
      finish();
      return false;
    }
  }

  const isMixer =
    event?.type === 'mixer' ||
    event?.mode === 'mixer' ||
    String(eventId).startsWith('mixer-');

  if (isMixer) {
    const allowed = Array.isArray(event?.allowedGenders)
      ? event.allowedGenders
      : [];

    if (allowed.length) {
      const userG = normalizeGender(
        currentUser?.gender ||
          localStorage.getItem('gender') ||
          ''
      );

      if (!allowed.includes(userG)) {
        setDeniedOrientation(
          orientationForSheetFromMixer(event)
        );
        setShowOrientationDenied(true);
        finish();
        return false;
      }
    }
  }

  if (isRevealEvent(event)) {
    const gender =
      currentUser?.gender ||
      localStorage.getItem('gender');

    if (!passesRevealGenderGate(event, gender)) {
      setDeniedOrientation(
        event?.revealMode?.orientation || 'open'
      );
      setShowOrientationDenied(true);
      finish();
      return false;
    }
  }

  finish();
  return true;
}

          
          /* ───────── confirmed REGISTER ───────── */
          async function confirmRegister() {
            const auth = requireAuth();
            if (!auth) return;
                if (registerBusy) return;
                setRegisterBusy(true);
            try {
              const firstReservationFallback =
                wasFirstReservationBeforeBooking();

              const registerUrl =
                event.type === 'mixer'
                  ? `${process.env.REACT_APP_API_URL}/mixer/events/${eventId.replace('mixer-', '')}/checkin`
                  : `${process.env.REACT_APP_API_URL}/events/${eventId}/register`;
         const { data } = await axios.post(
  registerUrl,
  { userId: auth.uid },
  {
    headers: {
      Authorization: `Bearer ${auth.token}`,
    },
  }
);

const bookedGatheringId = String(
  event?._id || eventId
).replace(/^mixer-/, "");

setHostAccess(!!data.hostAccess);

if (!data.hostAccess) {
  setRegistered(true);

  openReservationSuccess(
    bookedGatheringId,
    data?.event
      ? {
          ...event,
          ...data.event,
        }
      : event,
    typeof data?.isFirstReservation ===
      'boolean'
      ? data.isFirstReservation
      : firstReservationFallback
  );
}

const updatedEvent = data.event || data;

setEvent(prev => {
  const merged = {
    ...prev,
    ...updatedEvent,
    promoterId: typeof prev?.promoterId === 'object'
      ? prev.promoterId
      : updatedEvent.promoterId,
  };

  const ids = Array.isArray(merged.attendees)
    ? merged.attendees.map(a => String(a?._id ?? a))
    : Array.isArray(merged.attendeeIds)
    ? merged.attendeeIds.map(String)
    : [];

  return { ...merged, attendeeIds: ids, attendeeCount: ids.length };
});

              

        } catch (err) {
  const status = err?.response?.status;
  const reasonRaw = err?.response?.data?.reason || '';
  const reason = String(reasonRaw).toLowerCase();

  // ✅ AGE
  if (status === 403 && reason === 'age_gate') {
    const min = Number(event?.ageMin ?? 18);
    const max = event?.ageMax == null ? null : Number(event?.ageMax);
    setAgeGateLabel(labelFromRange(min, max));
    setShowAgeDenied(true);
    setShowConfirmSheet(false);
    return;
  }

  // ✅ Audience deny (Classic / Mixer / Neutral)
  if (status === 403 && (reason === 'gender_gate' || reason === 'orientation_gate')) {
    const isNeutral = String(event?.mode || '').toLowerCase() === 'neutral';
    const isMixer =
      (event?.type === 'mixer' || event?.mode === 'mixer' || String(eventId).startsWith('mixer-'));
    const isClassic = String(event?.mode || '').toLowerCase() === 'matchmaking';

    if (isNeutral) setDeniedOrientation(orientationForSheetFromNeutral(event));
    else if (isMixer) setDeniedOrientation(orientationForSheetFromMixer(event));
    else if (isClassic) setDeniedOrientation(orientationForSheetFromClassic(event));
    else setDeniedOrientation('open');

    setShowOrientationDenied(true);
    setShowConfirmSheet(false);
    return;
  }

  // ✅ Lane full / sold out (optional)
  if (status === 409 && (reason === 'lane_full' || reason === 'sold_out' || reason === 'full')) {
    alert(err?.response?.data?.message || 'This event is full.');
    setShowConfirmSheet(false);
    return;
  }

  // fallback
  alert(err?.response?.data?.message || err.message);
} finally {
  setShowConfirmSheet(false);
  setRegisterBusy(false);
}

          }

                   /* ——— confirmed UN-REGISTER / “Lose spot” ——— */
async function confirmCancel() {
  const auth = requireAuth();

  if (!auth || registerBusy) {
    return;
  }

  setRegisterBusy(true);

  try {
    const api = process.env.REACT_APP_API_URL;

    const resolvedId = String(
      event?._id || eventId
    ).replace(/^mixer-/, "");

    const { data } = await axios.post(
     `${api}/tickets/${resolvedId}/cancel`,
      null,
      {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      }
    );

    setRegistered(false);
    setHasTicket(false);

    if (data?.event) {
      setEvent((previousEvent) => ({
        ...previousEvent,
        ...data.event,
        promoterId:
          typeof previousEvent?.promoterId === "object"
            ? previousEvent.promoterId
            : data.event.promoterId,
      }));
    }

    const reservationUserId = String(
      localStorage.getItem("userId") ||
        auth.uid ||
        "anonymous"
    );

    localStorage.removeItem(
      `tribeReservationSuccessSeen:${reservationUserId}:${resolvedId}`
    );

    localStorage.removeItem(
      "tribePendingReservationSuccess"
    );

    window.dispatchEvent(
      new CustomEvent("ticket:invalidate", {
        detail: {
          eventId: resolvedId,
        },
      })
    );

    await fetchEvent();

    try {
      const membershipResponse = await axios.get(
        `${api}/subscriptions/me`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      setMembership(
        membershipResponse.data || null
      );
    } catch (membershipError) {
      console.warn(
        "[cancel] membership refresh failed",
        membershipError?.response?.data ||
          membershipError?.message
      );
    }
  } catch (err) {
    console.error("[cancel]", err);

    alert(
      err?.response?.data?.message ||
        err?.message ||
        "Could not cancel your reservation."
    );
  } finally {
    setShowCancelSheet(false);
    setRegisterBusy(false);
  }
}



          // ───────────────── enter / go‑live ─────────────────
          const handleEnter = async (e) => {
            e.stopPropagation();
          
            const mixer = event?.type === 'mixer' || event?.mode === 'mixer' || String(eventId).startsWith('mixer-');
            const neutral = String(event?.mode || '').toLowerCase() === 'neutral';

            const cleanEventId = String(eventId).replace('mixer-', '');
          
 // 1) ticket gate (fast client hint; server will re-check)
if (!hasTicket && !hostAccess) {
  setShowRegFailSheet(true);
  return;
}

          
            // Neutral entry requires a ticket or host access, without a geofence.
            if (neutral) {
              navigate(`/neutral/${eventId}/intro`, { state: { event } });
              return;
            }

            // Legacy mode geofence hint; neutral returns before this block.
            const userLat = Number(localStorage.getItem('userLatitude'));
            const userLng = Number(localStorage.getItem('userLongitude'));
            const distance = haversineMi(userLat, userLng, event.latitude, event.longitude);
            const radiusInMiles = event.radius ? event.radius / 5280 : 0;
            const withinRadius =
              !radiusInMiles ||
              (!Number.isNaN(userLat) && !Number.isNaN(userLng) && distance <= radiusInMiles);
            if (!withinRadius) {
              setShowGeoSheet(true);
              // NOTE: don’t return here for Reveal — we’ll still ask the server below
            }
          
            // 2.5) 🔐 REVEAL hard gates on ENTER (server is the source of truth)
            if (isRevealEvent(event)) {
              if (!hasTicket) { setShowRegFailSheet(true); return; }

              const cleanRevealId = String(eventId).replace('reveal-', '');
          
              try {
                const res = await axios.get(
                  `${process.env.REACT_APP_API_URL}/reveal-mode/${cleanRevealId}/access`,
                  {
                    validateStatus: (s) => s === 200 || s === 401 || s === 403,
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      'X-Suppress-Global-Error': '1', // so global interceptor doesn’t toast
                    },
                  }
                );
          
                // ✅ Allowed: route by quiz state
                if (res.status === 200 && res.data?.ok) {
                  if (res.data.alreadySubmitted) {
                    navigate(`/reveal/${cleanRevealId}/portal`, { state: { event } });
                    return;
                  }
                  navigate(`/reveal/${cleanRevealId}/intro`, { state: { event } });
                  return;
                }
          
                // ❌ Denied: branch by reason → open correct UI
                const reason = res?.data?.reason || '';
                switch (reason) {
                  case 'out_of_fence':
                  case 'no_location':
                    setShowGeoSheet(true);
                    return;
                  case 'not_registered':
                    setShowRegFailSheet(true);
                    return;
                  case 'quiz_required':
                  case 'quiz_incomplete':
                    navigate(`/reveal/${cleanRevealId}/intro`, { state: { event } });
                    return;
                    case 'age_gate':
     setAgeGateLabel(labelFromRange(
       Number(event?.ageMin ?? 18),
       event?.ageMax == null ? null : Number(event?.ageMax)
     ));
     setShowAgeDenied(true);
     return;
                  case 'gender_gate':
                    setDeniedOrientation(event?.revealMode?.orientation || 'open');
                      setShowOrientationDenied(true);
                      return;
                  case 'too_early':
                    // optional: show a “too early” sheet or let portal countdown handle it
                    return;
                  default:
                    // optional fallback UI
                    return;
                }
              } catch (_err) {
                // Network/unexpected error — optional toast or silent fail
                return;
              }
            }
          
            // 3) mixer vs classic diverge
            if (mixer) {
              // ✨ HETERO MIXER: skip quiz and go straight to portal
              if (isHeteroMixer) {
                navigate(`/mixer/events/${cleanEventId}`);
                return;
              }
          
              // Default (non-hetero) mixer: check quiz state as before
              try {
                const { data: quizData } = await mixerApi.fetchQuiz(cleanEventId, currentUser._id);
                if (quizData.alreadySubmitted) {
                  navigate(`/mixer/events/${cleanEventId}`);
                } else {
                  navigate('/intro-mixer', { state: { event } });
                }
              } catch (err) {
                console.error('[handleEnter] mixer quiz check failed:', err);
                setError('Could not verify quiz status. Please try again.');
              }
              return;
            }

            // REGULAR events → intro wizard, not portal
            navigate('/intro', { state: { event } });
          };
          
          
  return (
    <div style={S.root}>
     <div ref={topRef} />

     <EventHeader
  onBack={() => navigate('/events')}
  isSaved={isSaved}
  onToggleSave={handleSave} // reuse your existing save logic
/>

<HostAccessBanner
  show={hostAccess}
  hostName={(currentUser?.firstName || '').trim()}
/>


     
<EventDetails
  event={{
    ...displayEvent,
    currentUser: {
      gender: (
        currentUser?.gender || ''
      ).toLowerCase(),
    },
  }}
  ticketRefreshKey={ticketRefreshKey}
  isAuthed={!!currentUser?._id}
  isHostViewer={hostAccess}
  isRecommended={
    location.state?.isRecommended === true
  }
/>

      <EventTags event={displayEvent}/>

      {/* Main */}
      <main style={S.content}>
       
      </main>


{/* 
<EventFooterAction
  eventStarted={eventStarted}
  isRegistered={isRegistered}
  hostAccess={hostAccess}
  onEnter={handleEnter}
  onRegister={initiateRegister}
  registerDisabled={registerBusy}
  price={event?.price}
  taxesText="Taxes included"
  policyText="Non-refundable"
  isSoldOut={!!event?.isSoldOut}
  displayCapacity={event?.displayCapacity}
  displayAttendeeCount={event?.displayAttendeeCount}
  capacity={event?.capacity}
  attendeeCount={event?.attendeeCount}
/> 
*/}

<MembershipEventFooterAction
  autoOpenConfirm={
    state?.openReservationSheet === true
  }

  hasAccount={Boolean(userId)}

  portalOpen={portalOpen}
  eventStarted={eventStarted}

  isRegistered={isRegistered}
  hostAccess={hostAccess}
  onEnter={handleEnter}
  onBeforeReserve={runReservationPreflight}
  onRegister={reserveWithMembership}
  onCancel={() => setShowCancelSheet(true)}
  registerDisabled={registerBusy}

  membershipActive={hasActiveMembership}
  membershipStatus={
    membership?.membershipStatus || ''
  }
  reservationAccessStatus={
    membership?.reservationAccessStatus || ''
  }
  membershipCardRequired={
    membership?.membershipCardRequired === true
  }
  membershipCardRequiresReplacement={
    membership?.membershipCardRequiresReplacement === true
  }
  attendanceWarning={
    membership?.attendanceWarning === true
  }
  reservationAttendanceStreak={
    membership?.reservationAttendanceStreak || 0
  }
  membershipUsed={
    membership?.membershipReservationsUsed || 0
  }
  membershipLimit={
    membership?.membershipReservationLimit || 3
  }

  isSoldOut={!!event?.isSoldOut}
  displayCapacity={event?.displayCapacity}
  displayAttendeeCount={
    event?.displayAttendeeCount
  }
  capacity={event?.capacity}
  attendeeCount={event?.attendeeCount}
/>
  
{showConfirmSheet && (
  <ConfirmRegistrationBottomSheet
    eventName={name}
    onConfirm={confirmRegister}
    onDismiss={() => setShowConfirmSheet(false)}
  />
)}
{showCancelSheet && (
  <CancelRegistrationBottomSheet
    eventName={name}
    onConfirm={confirmCancel}
    onDismiss={() => setShowCancelSheet(false)}
  />
)}
{showGeoSheet && (
  <NotInEventRadiusBottomSheet
    onNavigate={() =>
      window.open(`https://maps.google.com/?q=${latitude},${longitude}`)}
    onDone={() => setShowGeoSheet(false)}
  />
)}

{showRegFailSheet && (
  <EventAlreadyStartedBottomSheet
    eventName={name}
    onDismiss={() => setShowRegFailSheet(false)}
  />
)}
{/* Hard-gate deny (orientation) */}
{showOrientationDenied && (
  <OrientationGateDeniedBottomSheet
    orientation={
      deniedOrientation ||
      event?.revealMode?.orientation ||
      event?.orientationType ||
      event?.classicOrientation || 'open'
    }
    onDismiss={() => setShowOrientationDenied(false)}
    onExploreEvents={() => {
      setShowOrientationDenied(false);
      navigate('/events');
    }}
  />
)}
{showAgeDenied && (
  <AgeGateDeniedBottomSheet
    label={ageGateLabel}
    onDismiss={() => setShowAgeDenied(false)}
    onExplore={() => {
      setShowAgeDenied(false);
      navigate('/events');
    }}
  />
)}


<PostReservationSuccessSheet
  isOpen={showReservationSuccess}
  gathering={
    reservationSuccessGathering || event
  }
  onClose={() =>
    setShowReservationSuccess(false)
  }
  onViewGathering={() =>
    setShowReservationSuccess(false)
  }
/>

{/* Soft-gate confirm */}
{showAffinitySoftGate && (
  <AffinitySoftGateSheet
    affinity={!!affinitySoftGateCtx?.affinity}
    restricted={affinitySoftGateCtx?.restricted !== false} // default true
    onCancel={() => setShowAffinitySoftGate(false)}
    onConfirm={() => {
      

      setShowAffinitySoftGate(false);
      navigate(`/events/${eventId}/purchase`, { state: { event, fromEventPage: true } });
    }}
  />
)}





    </div>
  );
}

/* ────────────────────────────────────────────────
 *  Styles
 * ──────────────────────────────────────────────── */
const S = {
    root: {
      fontFamily: 'Inter, Arial, sans-serif',
      background: '#fff',
      minHeight: '100vh',
      fontFamilyBase: "'Open Sans', sans-serif",
      overflowAnchor: 'none',

    },

  
    content: {
      padding: '0 20px 120px',
      fontFamilyBase: "'Open Sans', sans-serif",

    },
  
    titleRow: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      fontFamilyBase: "'Open Sans', sans-serif",

    },
  
    title: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.5,
      marginRight: 12,
      marginTop: 0,
      fontFamilyBase: "'Open Sans', sans-serif",

    },
  
    dateBadge: {
      background: '#397AFF',
      color: '#fff',
      borderRadius: 8,
      width: 70,
      textAlign: 'center',
      padding: '6px 0px',
   
    },
  
    badgeMonth: {
      display: 'block',
      fontSize: 12,
      fontWeight: 600,
    },
  
    badgeDay: {
      display: 'block',
      fontSize: 22,
      fontWeight: 700,
      marginTop: -2,
    },
    hostRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 0,
      },
      
      hostAvatar: {
        width: 30,
        height: 30,
        borderRadius: '50%',
        objectFit: 'cover',
        flexShrink: 0,
        border: 'solid 1px #eee'
      },
      
      hostText: {
        fontSize: 14,
        color: '#555',
        lineHeight: 1.3,
      },
      
      hostName: {
        fontWeight: 700,
        color: '#000',   // darker + heavier
      },
  
    desc: {
      marginTop: 16,
      fontSize: 14,
      lineHeight: 1.5,
      color: '#333',
    },
  
    infoRow: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 14,
      marginTop: 18,
      color: '#111',
      gap: 10,
    },
  
    infoIcon: {
      fontSize: 18,
    },
  
    mapBtn: {
      marginLeft: 'auto',
      background: '#e0e0e0',
      border: 'none',
      borderRadius: 20,
      width: 28,
      height: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },
  
    avatars: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 18,
    },
  
    avatar: {
      width: 30,
      height: 30,
      borderRadius: '50%',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      border: '2px solid #fff',
    },
  
    more: {
      width: 30,
      height: 30,
      borderRadius: '50%',
      background: '#eee',
      border: '1px solid #eee',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10,
      fontWeight: 550,
    },
  
  // ─── Header row ─────────────────────────────────
  eventTypeHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '8px 0 0px 0',   // no left/right margin
  },
  eventTypeHeadline: {
    margin: 0,                 // flush left
    fontSize: 16,
    fontWeight: 600,
    color: '#000',
    fontFamily: 'Inter, Arial, sans-serif',
    textTransform: 'none',
    textAlign: 'left',
  },
  eventTypeInfoBtn: {
    margin: 0,                 // flush right
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#555',
  },
  
  eventTypeHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  eventTypeIcon: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#397AFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    marginLeft: 0,

  },
// in your S = { … } block
eventTypeHeadline: {
    fontSize: 16,                // slightly smaller
    fontWeight: 600,             // a bit less bold
    margin: '24px 0px 16px',
    color: '#000',               // lighter grey
    fontFamily: 'Inter, Arial, sans-serif',
    textTransform: 'none',
    textAlign: 'left',
  },
  
  eventTypeInfoBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: '#000',
  },
  
  // ─── Card ────────────────────────────────────────
  eventTypeCard: {
    display: 'flex',
    alignItems: 'center',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    margin: '0 0px 0px',
  },
  eventTypeImage: {
    width: 120,
    height: 120,
    objectFit: 'cover',
    flexShrink: 0,
  },
  eventTypeCardContent: {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  eventTypeCardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: '#222',
  },
  eventTypeCardDesc: {
    margin: '8px 0 16px',
    fontSize: 12,
    color: '#555',
    flex: 1,
  },
  eventTypeCardBtn: {
    alignSelf: 'flex-start',
    padding: '8px 16px',
    background: '#397AFF',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  
  eventTypeImagePlaceholder: {
    width: 120,
    height: 120,
    backgroundColor: '#f0f0f0',
    flexShrink: 0,
    borderRadius: 8,
    marginLeft: 8,
    marginTop: 8,
    marginBottom: 8,

  },
  
  bottom: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    display: 'flex',
    gap: 16,
    padding: 20,
    paddingBottom: 12,
    paddingTop: 12,
    background: '#fff',
  },

  enterBtn: {
    flex: 1,
    background: '#397AFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    padding: '14px 0',
    cursor: 'pointer',
    maxWidth: 'calc(100% - 40px)', // explicitly prevent overflow (20px padding left/right)
    boxSizing: 'border-box',
    height:40,
  },

  
    saveBtn: {
      flex: '0 0 30%',
      background: '#d9d9d9',
      border: 'none',
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      padding: '14px 0',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      color:'#000'
    },
  
    saveActive: {
      background: '#000',
      color: '#fff',
    },
  
    regBtn: {
      flex: 1,
      background: '#397AFF',
      border: 'none',
      borderRadius: 8,
      color: '#fff',
      fontSize: 12,
      fontWeight: 600,
      padding: '14px 0',
      cursor: 'pointer',
      marginRight:40,
    },
  
    regged: {
      background: '#E53935',
    },
    livePill: {
        position: 'absolute',
        right: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        background: '#E8F5E9',
        color: '#2E7D32',
        borderRadius: 20,
        padding: '4px 14px',
        fontSize: 14,
        fontWeight: 600,
      },
      
      spotsPill: {
        position: 'absolute',
        right: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        background: '#f0f0f0',
        color: '#222',
        borderRadius: 20,
        padding: '8px 14px',
        fontSize: 12,
        fontWeight: 600,
      },
      
      spotsTxt: {
        fontWeight: 500,
        fontSize: 12,
      },
      
      fullPill: {
        position: 'absolute',
        right: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        background: '#FFEBEE',
        color: '#C62828',
        borderRadius: 20,
        padding: '8px 14px',
        fontSize: 12,
        fontWeight: 600,
      },
   
countPill: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 34,
    padding: '0 10px',
    borderRadius: 9999,          // pill shape
    backgroundColor: '#4285F4',  // blue like EventCard
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 600,
    border: '1px solid #fff',
    boxSizing: 'border-box',
    zIndex: 5,
  },
  actionBar: {
  position: 'fixed',
  inset: 'auto 0 0 0',
  background: '#fff',
  borderTop: '1px solid #eef0f2',
  display: 'flex',
  gap: 10,
  padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
  // prevent horizontal overflow & center on large screens
  boxSizing: 'border-box',
  maxWidth: 760,
  margin: '0 auto',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  zIndex: 50,
},
actionBar: {
  position: 'fixed',
  inset: 'auto 0 0 0',
  background: '#fff',
  borderTop: '1px solid #eef0f2',
  display: 'flex',
  gap: 10,
  padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
  // prevent horizontal overflow & center on large screens
  boxSizing: 'border-box',
  maxWidth: 760,
  margin: '0 auto',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  zIndex: 50,
},

primaryBtn: {
  flex: 1,
  height: 44,
  borderRadius: 10,
  border: '1px solid #0b0b0b',
  background: '#0b0b0b',
  color: '#fff',
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
},

dangerBtn: {
  borderColor: '#b91c1c',
  background: '#b91c1c',
},

ghostBtn: {
  flex: '0 0 36%',
  minWidth: 120,
  height: 44,
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#0b0b0b',
  fontSize: 14,
  fontWeight: 800,
  letterSpacing: 0.2,
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  cursor: 'pointer',
},

ghostActive: {
  borderColor: '#0b0b0b',
  background: '#0b0b0b',
  color: '#fff',
},

icon: { fontSize: 16, flexShrink: 0 },


  };
